import type { DocumentType, PeopleData, RoutingConfig, TemplateConfig, WorkflowDefinition } from '@lg/shared';

/**
 * Workflow hand-off file for one venue company: which template workflow goes
 * into which company, how it routes (steps, roles, escalation), the venue's
 * approval limits for that document, and the manual import steps in D365.
 *
 * The workflow itself is moved with D365's own export/import (the template
 * company's export is the file that gets imported); this file is the app's
 * instruction sheet for that import, so an admin can do it, and check it,
 * without opening the workflow editor. Nothing is imported automatically.
 */

export interface WorkflowXmlInput {
  workflow: WorkflowDefinition;
  template: TemplateConfig;
  routing: RoutingConfig;
  companyId: string;
  legalName: string;
  requestId: string;
  people: PeopleData | null;
  generatedAt: Date;
}

/** Where each document type's workflows live in D365 (menu path to the workflow list). */
const WORKFLOW_PAGE: Record<DocumentType, string> = {
  PurchaseRequisition: 'Procurement and sourcing > Setup > Procurement and sourcing workflows',
  PurchaseOrder: 'Procurement and sourcing > Setup > Procurement and sourcing workflows',
  VendorInvoice: 'Accounts payable > Setup > Accounts payable workflows',
  Journal: 'General ledger > Journal setup > General ledger workflows',
};

const esc = (s: string | number): string =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const attrs = (a: Record<string, string | number | boolean | null | undefined>): string =>
  Object.entries(a)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ` ${k}="${esc(String(v))}"`)
    .join('');

export function workflowFileName(companyId: string, workflow: WorkflowDefinition): string {
  return `${companyId}-${workflow.key}-workflow-v${workflow.version}.xml`;
}

export function buildWorkflowXml(input: WorkflowXmlInput): string {
  const { workflow: wf, template, companyId } = input;
  const roleName = new Map(template.roleCatalog.map((r) => [r.roleId, r.name]));
  const route = input.routing.documentTypes.find((d) => d.type === wf.documentType);
  const steps = route?.steps ?? [];
  const usesLimits = steps.some((s) => s.kind === 'managerial');
  const sourceFile = wf.xmlBlobUrl.split('/').pop() ?? `${wf.key}-v${wf.version}.xml`;
  const L: string[] = [];

  L.push('<?xml version="1.0" encoding="utf-8"?>');
  L.push(
    `<!-- ${esc(wf.name)} for ${esc(companyId)}. The workflow is imported with D365's own Import, from the`,
    `     export of ${esc(sourceFile)} taken in ${esc(template.templateLegalEntityId)}. This file says where it goes,`,
    '     how it routes and how to check it. Generated by Legends venue onboarding. -->',
  );
  L.push(
    `<VenueWorkflow${attrs({
      xmlns: 'urn:legends:venue-onboarding:workflow:1',
      generated: input.generatedAt.toISOString(),
      request: input.requestId,
    })}>`,
  );
  L.push(`  <Target${attrs({ legalEntity: companyId, name: input.legalName })}/>`);
  L.push(
    `  <Source${attrs({
      template: template.id,
      legalEntity: template.templateLegalEntityId,
      workflow: wf.key,
      version: wf.version,
      exportFile: sourceFile,
    })}/>`,
  );
  L.push(
    `  <Workflow${attrs({
      key: wf.key,
      name: wf.name,
      documentType: wf.documentType,
      activate: true,
    })}>`,
  );
  L.push('    <Steps>');
  steps.forEach((s, i) => {
    if (s.kind === 'role') {
      L.push(
        `      <ApprovalStep${attrs({ sequence: i + 1, name: s.label, assignTo: 'SecurityRole' })}>`,
        `        <Role${attrs({ id: s.roleId, name: roleName.get(s.roleId) ?? s.roleId, scopedTo: companyId })}/>`,
        '      </ApprovalStep>',
      );
    } else {
      L.push(
        `      <ApprovalStep${attrs({ sequence: i + 1, name: s.label, assignTo: 'ManagerialHierarchy' })}>`,
        `        <Hierarchy${attrs({
          type: 'Position',
          startFrom: 'Submitter',
          stopWhen: 'SigningLimitCoversAmount',
          assign: s.assign === 'lastRetrieved' ? 'LastRetrievedUser' : 'AllRetrievedUsers',
        })}/>`,
        '      </ApprovalStep>',
      );
    }
  });
  L.push('    </Steps>');
  if (route)
    L.push(
      `    <Escalation${attrs({
        role: route.escalationRoleId,
        name: roleName.get(route.escalationRoleId) ?? route.escalationRoleId,
      })}/>`,
    );
  L.push('  </Workflow>');

  // The venue-specific part: limits and who holds each job. The workflow file itself stays the same.
  if (usesLimits) {
    const limits = (input.people?.approvalLimits ?? []).filter((l) => l.documentType === wf.documentType);
    L.push(
      `  <ApprovalLimits${attrs({ documentType: wf.documentType, currency: 'USD', company: companyId })}>`,
    );
    if (limits.length === 0)
      L.push('    <!-- No people import yet: limits come from the People and approvals step. -->');
    for (const l of [...limits].sort((a, b) => a.amountUsd - b.amountUsd)) {
      const positions = (input.people?.positions ?? []).filter((p) => p.jobId === l.jobId);
      L.push(`    <Limit${attrs({ job: l.jobId, amount: l.amountUsd.toFixed(2) })}>`);
      for (const p of positions) {
        // Any holder (a person can hold a second, non-primary position: the GM who is also GL accountant).
        const held = input.people?.assignments.filter((x) => x.positionId === p.positionId) ?? [];
        const a = held.find((x) => x.primary) ?? held[0];
        const w = a ? input.people?.workers.find((x) => x.personnelNumber === a.personnelNumber) : undefined;
        L.push(
          `      <Position${attrs({
            id: p.positionId,
            title: p.title,
            reportsTo: p.reportsToPositionId,
            holder: w ? `${w.firstName} ${w.lastName}` : 'vacant',
            personnelNumber: w?.personnelNumber,
          })}/>`,
        );
      }
      L.push('    </Limit>');
    }
    L.push('  </ApprovalLimits>');
  }

  const roleHolders = steps
    .filter((s) => s.kind === 'role')
    .flatMap((s) =>
      (input.people?.roles ?? []).filter((r) => r.roleId === (s.kind === 'role' ? s.roleId : '')),
    );
  if (roleHolders.length) {
    L.push('  <RoleHolders>');
    for (const r of roleHolders)
      L.push(`    <User${attrs({ upn: r.upn, role: r.roleId, company: companyId })}/>`);
    L.push('  </RoleHolders>');
  }

  const steps2 = [
    `In ${template.templateLegalEntityId}, open ${WORKFLOW_PAGE[wf.documentType]}, select "${wf.name}" and use Export. Keep the file (${sourceFile}).`,
    `Switch the company to ${companyId}.`,
    `Open ${WORKFLOW_PAGE[wf.documentType]} and use Import with that file.`,
    `Open the imported workflow, check the steps match <Steps> above (no named users, roles scoped to ${companyId}), then Activate the version.`,
    usesLimits
      ? `Confirm ${companyId} is in the signing limit policy and the limits match <ApprovalLimits>.`
      : `Confirm the users in <RoleHolders> hold their role, limited to ${companyId}.`,
    `Submit a test ${wf.documentType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()} in ${companyId} and check who receives it.`,
  ];
  L.push('  <ImportSteps>');
  const text = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  steps2.forEach((t, i) => L.push(`    <Step${attrs({ n: i + 1 })}>${text(t)}</Step>`));
  L.push('  </ImportSteps>');
  L.push('</VenueWorkflow>');
  return L.join('\n') + '\n';
}
